import { type FC, type PropsWithChildren, lazy, Suspense } from "react";
import { matchPath, useLocation } from "react-router-dom-v5-compat";
import type { ConfigSectionProps } from "./ConfigSection";
import type { DevMenuAppInfo } from "./DevMenu";
import type { FlagsAdapter } from "./DevToolInner";

/**
 * Wraps children with an overridden flags context for dev/staging.
 * For @apac/feature-flags tenants: render inside FeatureFlagsProvider (default).
 * For @hopper-b2b/experiments tenants: pass flagsAdapter={createContextAdapter(ExperimentsContext)}.
 *
 * DCE (Dead Code Elimination) strategy:
 * 1. `import.meta.env.PROD` is the HARD gate — Vite replaces it at build
 *    time, so Rollup eliminates the lazy import and the entire DevToolInner
 *    module graph in production builds.
 * 2. `VITE_DEVTOOL=true` is an opt-in escape hatch for apps that set
 *    NODE_ENV=production in non-production builds (e.g., virgin-au staging).
 *    When set, DevTool code is kept in the bundle regardless of PROD.
 * 3. `isProduction` prop is a SOFT runtime gate for cases where a non-production
 *    build is deployed to a production domain (e.g., staging build on prod URL).
 *    This prevents the dev tool from rendering but does NOT affect bundling.
 */

const SHOULD_ENABLE_DEVTOOL =
  !import.meta.env.PROD || import.meta.env.VITE_DEVTOOL === "true";

export type DevToolProps = PropsWithChildren<{
  appInfo?: DevMenuAppInfo;
  isProduction?: boolean;
  /** Disable on these route patterns and their descendants. Opt-in; requires a router when non-empty. */
  disabledPaths?: string[];
  authInfo?: ConfigSectionProps["authInfo"];
  locale?: ConfigSectionProps["locale"];
  showLanguagePicker?: boolean;
  fabBottom?: number;
  flagsAdapter?: FlagsAdapter;
  mockLoginUrl?: ConfigSectionProps["mockLoginUrl"];
  mockLoginActions?: ConfigSectionProps["mockLoginActions"];
}>;

const Passthrough: FC<PropsWithChildren> = ({ children }) => <>{children}</>;

export const DevTool: FC<DevToolProps> = SHOULD_ENABLE_DEVTOOL
  ? DevToolWrapper
  : Passthrough;

// Everything below is unreachable in production builds (Vite replaces
// `import.meta.env.PROD` with `true`, so when PROD=true and VITE_DEVTOOL
// is absent the ternary always picks Passthrough). Rollup eliminates
// DevToolWrapper, LazyDevToolInner, and the entire
// DevToolInner → DevMenu → FeatureFlagsSection → theme module graph.

const LazyDevToolInner = lazy(() => import("./DevToolInner"));

function DevToolWrapper(props: DevToolProps) {
  // Soft runtime gate: hide devtool on production domains even in non-prod builds
  if (!SHOULD_ENABLE_DEVTOOL || props.isProduction) {
    return <>{props.children}</>;
  }

  return props.disabledPaths?.length ? (
    <PathAwareDevTool {...props} disabledPaths={props.disabledPaths} />
  ) : (
    <EnabledDevTool {...props} />
  );
}

function PathAwareDevTool(props: DevToolProps & { disabledPaths: string[] }) {
  const { pathname } = useLocation();
  const isDisabled = props.disabledPaths.some((path) =>
    matchPath({ path, end: false }, pathname)
  );

  return isDisabled ? <>{props.children}</> : <EnabledDevTool {...props} />;
}

function EnabledDevTool({
  children,
  appInfo,
  authInfo,
  locale,
  showLanguagePicker,
  fabBottom,
  flagsAdapter,
  mockLoginUrl,
  mockLoginActions,
}: DevToolProps) {
  return (
    <Suspense fallback={<>{children}</>}>
      <LazyDevToolInner
        appInfo={appInfo}
        authInfo={authInfo}
        locale={locale}
        showLanguagePicker={showLanguagePicker}
        fabBottom={fabBottom}
        flagsAdapter={flagsAdapter}
        mockLoginUrl={mockLoginUrl}
        mockLoginActions={mockLoginActions}
      >
        {children}
      </LazyDevToolInner>
    </Suspense>
  );
}
